import type { Review } from '@/scopes/products/user-reviews/types'
import { useIntl } from '@/intl'
import { ReadMore } from '@/scopes/common/components/ReadMore'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { resolveLanguageCode } from '@/scopes/products/pages/components/HeroSection/Reviews/UserReview/utils'
import { createUserReviewsEvent } from '@/scopes/products/user-reviews/infrastructure/analytics'
import { space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Button, Icon, Typography } from '@sunwebgroup/ui-components/uds'
import { useState } from 'react'
import { styled } from 'styled-components'
import ReviewerInfo from '../ReviewerInfo'
import { getFlagCode, getPartyCompositionType } from '../utils'
import { ReviewScore } from './ReviewScore'
import { TranslatedByAtribution } from './TranslatedByAttribution'
import { TranslatedText } from './TranslatedText'

export const Card = styled.div`
  padding: ${space('primitives_space_16')};
  border-radius: ${tokPx('primitives_corner_radius_l')};
  background-color: ${tok('primitives_color_neutral_0')};
  border: ${tokPx('semantics_dividers_tickness')} solid ${tok('primitives_color_neutral_100')};
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_16')};

  &:not(:last-child) {
    margin-bottom: ${space('primitives_space_12')};
  }
`

const HeaderWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`

const StrongText = styled(Typography)`
  color: ${tok('semantics_content_color')};
`
const TranslateButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokPx('primitives_space_8')};
  justify-content: space-between;
`

export function UserReview({
  date,
  rating,
  scoreLabel,
  description,
  reviewLanguageCode,
  userName,
  partyCompositionGroupId,
  id,
  departureDate,
}: Review) {
  const intl = useIntl()
  const [showTranslatedText, setShowTranslatedText] = useState(false)
  const { sendEvent } = useAnalytics()
  const siteLanguage = resolveLanguageCode(useConfiguration('locale'))

  // If the date is empty (shown as 1970-01-01) we want to show the departure date instead of the review date
  const resolveDate = () => {
    const invalidDate = new Date('1970-01-01')

    if (Number(date) === Number(invalidDate))
      return departureDate

    return intl.formatRelativeDate(date)
  }

  const handleToggleTranslation = () => {
    // There is a typo in the event name "reviewTransalted" that we need to keep for backwards compatibility. Once we remove legacy components we can fix it.
    sendEvent(
      createUserReviewsEvent({
        description: 'Show review translations',
        element: 'Clickable text',
        result: showTranslatedText ? 'original' : 'translated',
        ecommerce: {
          reviewTransalted: {
            id,
            overallRating: Number.parseFloat(rating),
            country: reviewLanguageCode,
            partyComposition: intl.formatMessage({
              id: `reviews_party_composition_${getPartyCompositionType(partyCompositionGroupId)}_label`,
            }),
          },
        },
      }),
    )
    setShowTranslatedText(!showTranslatedText)
  }

  return (
    <Card>
      <HeaderWrapper>
        <ReviewScore rating={rating} scoreLabel={scoreLabel} />
        <StrongText $variant="body-m-regular-tight">{resolveDate()}</StrongText>
      </HeaderWrapper>
      {showTranslatedText && <TranslatedText>{description}</TranslatedText>}
      {!showTranslatedText && (
        <ReadMore charactersShown={255} readMoreText={intl.formatMessage({ id: 'reviews_read_more_label' })}>
          {description || description !== '' ? description : intl.formatMessage({ id: 'reviews_no_text' })}
        </ReadMore>
      )}
      {reviewLanguageCode !== siteLanguage && showTranslatedText && (
        <TranslateButtonWrapper>
          <Button $type="transparentNeutral" onClick={handleToggleTranslation} $size="XS">
            <Icon glyph="LanguageTranslate" />
            <Typography>
              {intl.formatMessage({ id: 'reviews_translation_original' })}
            </Typography>
          </Button>
          <TranslatedByAtribution />
        </TranslateButtonWrapper>
      )}
      {reviewLanguageCode !== siteLanguage && !showTranslatedText && (
        <TranslateButtonWrapper>
          <Button $type="transparentNeutral" onClick={handleToggleTranslation} $size="XS">
            <Icon glyph="LanguageTranslate" />
            <Typography>
              {intl.formatMessage({ id: 'reviews_translation_translate' })}
            </Typography>
          </Button>
        </TranslateButtonWrapper>
      )}
      <ReviewerInfo
        reviewerName={userName || intl.formatMessage({ id: 'reviews_anonymous_user' })}
        flagCode={getFlagCode(reviewLanguageCode)}
        partyCompositionLabel={intl.formatMessage({ id: `reviews_party_composition_${getPartyCompositionType(partyCompositionGroupId)}_label` })}
        isBigWrapper
      />
    </Card>
  )
}
